import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { createPricingStrategy } from '@/api/generated/sdk.gen';
import { pricingStrategiesQuery, type PricingStrategy } from '@/lib/pricing';
import { messageFromError } from '@/lib/api';
import { usePermissions } from '@/lib/me';
import { AccessDenied, errorStatus } from '@/components/RouteError';
import { usePageHeader } from '@/components/PageHeader';
import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';

export const Route = createFileRoute('/_app/pricing-strategies/')({
  loader: ({ context }) => context.queryClient.ensureQueryData(pricingStrategiesQuery),
  component: PricingStrategiesPage,
});

function PricingStrategiesPage() {
  const { data, isLoading, isError, error } = useQuery(pricingStrategiesQuery);
  const { can } = usePermissions();
  const [showNew, setShowNew] = useState(false);
  const strategies = data ?? [];

  usePageHeader({
    subtitle:
      strategies.length > 0
        ? `${strategies.length} strateg${strategies.length === 1 ? 'y' : 'ies'}`
        : undefined,
    actions: can('pricing.write') ? (
      <Button variant="primary" size="sm" onClick={() => setShowNew(true)}>
        <Plus className="h-4 w-4" />
        New strategy
      </Button>
    ) : undefined,
  });

  return (
    <div>
      {isLoading ? (
        <p className="text-sm text-muted">Loading strategies…</p>
      ) : isError ? (
        errorStatus(error) === 403 ? (
          <AccessDenied />
        ) : (
          <p role="alert" className="text-sm text-destructive">
            {messageFromError(error)}
          </p>
        )
      ) : strategies.length === 0 ? (
        <EmptyState onNew={can('pricing.write') ? () => setShowNew(true) : undefined} />
      ) : (
        <StrategiesTable strategies={strategies} />
      )}

      {showNew && <NewStrategyDialog onClose={() => setShowNew(false)} />}
    </div>
  );
}

function EmptyState({ onNew }: { onNew?: () => void }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-dashed border-[var(--border-hairline)] bg-card p-10 text-center">
      <h2 className="text-sm font-semibold text-foreground">No pricing strategies yet</h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted">
        A strategy is a reusable set of rules (seasonal, day-of-week, gap-night, lead-time…) you
        assign to apartments. Create one, then add rules and assign it from an apartment’s Pricing
        section.
      </p>
      {onNew && (
        <Button variant="primary" size="sm" className="mt-4" onClick={onNew}>
          <Plus className="h-4 w-4" />
          New strategy
        </Button>
      )}
    </div>
  );
}

export function StrategiesTable({ strategies }: { strategies: PricingStrategy[] }) {
  return (
    <div className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--border-hairline)] text-left text-xs uppercase tracking-[0.06em] text-muted">
            <th className="px-4 py-2.5 font-semibold">Name</th>
            <th className="px-4 py-2.5 font-semibold">Default min-stay</th>
            <th className="px-4 py-2.5 font-semibold">Rules</th>
            <th className="px-4 py-2.5 font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {strategies.map((s) => (
            <tr key={s.id} className="border-t border-[var(--border-hairline)] hover:bg-[var(--surface-sunken)]">
              <td className="px-4 py-3">
                <Link
                  to="/pricing-strategies/$id"
                  params={{ id: s.id }}
                  className="font-medium text-primary hover:underline"
                >
                  {s.name}
                </Link>
              </td>
              <td className="px-4 py-3 tabular-nums">{s.default_min_stay} {s.default_min_stay === 1 ? 'night' : 'nights'}</td>
              <td className="px-4 py-3 tabular-nums">{s.rules.length}</td>
              <td className="px-4 py-3">
                {s.enabled ? (
                  <Badge variant="success" dot>
                    Active
                  </Badge>
                ) : (
                  <Badge variant="neutral" dot>
                    Disabled
                  </Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function NewStrategyDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [minStay, setMinStay] = useState('2');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      const trimmed = name.trim();
      if (!trimmed) throw new Error('Name is required.');
      const ms = Number.parseInt(minStay, 10);
      if (!Number.isFinite(ms) || ms < 1) throw new Error('Default min-stay must be at least 1 night.');
      const res = await createPricingStrategy({
        throwOnError: true,
        body: { name: trimmed, default_min_stay: ms, rules: [] },
      });
      return res.data;
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['pricing-strategies'] });
      toast({ kind: 'success', title: 'Strategy created', message: created.name });
      onClose();
      navigate({ to: '/pricing-strategies/$id', params: { id: created.id } });
    },
    onError: (err) => setError(messageFromError(err)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="New pricing strategy"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={mut.isPending}
            onClick={() => {
              setError(null);
              mut.mutate();
            }}
          >
            Create
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Standard, Cheap, Peak season"
          required
          autoFocus
        />
        <Input
          label="Default min-stay (nights)"
          type="number"
          min={1}
          value={minStay}
          onChange={(e) => setMinStay(e.target.value)}
          hint="Applied to every date unless a rule raises it."
          required
        />
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
